import { type z } from "zod";

const apiBase = (import.meta.env.VITE_ADMIN_API_BASE_URL ?? "/api/v1").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  init?: RequestInit,
): Promise<z.infer<TSchema>> {
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

  if (!res.ok) {
    let body: unknown = undefined;
    try {
      body = await res.json();
    } catch {
      // ignore non-JSON errors
    }
    const message =
      body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  // NestJS sometimes returns 200 + empty body for void handlers
  // (DELETE / PATCH no-content style). Treat any empty body the same
  // as 204 so the schema parse doesn't blow up on "".
  if (res.status === 204) return undefined as z.infer<TSchema>;
  const text = await res.text();
  if (text.length === 0) return undefined as z.infer<TSchema>;
  return schema.parse(JSON.parse(text));
}
